import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { WIDGETS } from "@tSurya/theme/design";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect } from "react";
import { css } from "styled-components";

const W = (WIDGETS as any).swatchGrid || {};

const enum fields {
    preTitle = "preTitle",
    title = "title",
    mobileDisplayMode = "mobileDisplayMode",
    ctaLabel = "ctaLabel",
    ctaLink = "ctaLink",
    items = "items",
    backgroundColor = "backgroundColor",
    customCSS = "customCSS",
}

interface SwatchItem {
    image?: string;
    label?: string;
}

interface SwatchItemModel {
    fields: SwatchItem;
}

interface SurSwatchGridProps extends WidgetProps {
    fields: {
        [fields.preTitle]: string;
        [fields.title]: string;
        [fields.mobileDisplayMode]: string;
        [fields.ctaLabel]: string;
        [fields.ctaLink]: LinkFieldValue;
        [fields.items]: SwatchItemModel[];
        [fields.backgroundColor]: string;
        [fields.customCSS]: string;
    };
}

export interface SurSwatchGridStyles {
    wrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    preTitle?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    gridContainer?: InjectableCss;
    gridRow?: InjectableCss;
    swatchCard?: InjectableCss;
    swatchImageWrapper?: InjectableCss;
    swatchLabel?: TypographyPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselViewport?: InjectableCss;
    carouselSlide?: InjectableCss;
    ctaWrapper?: InjectableCss;
    ctaButton?: InjectableCss;
}

export const surSwatchGridStyles: SurSwatchGridStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${W.gap || "32px"};
            padding: ${W.padding || "80px 40px"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: ${W.mobilePadding || "32px 16px"};
                    `,
                    css`
                        padding: ${W.mobilePadding || "32px 16px"};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${W.titlesGap || "4px"};
            text-align: center;
            width: 100%;
        `,
    },
    preTitle: {
        css: css`
            font-family: ${W.preTitle?.fontFamily || "'EB Garamond', serif"};
            font-style: ${W.preTitle?.fontStyle || "italic"};
            font-weight: ${W.preTitle?.fontWeight || "400"};
            font-size: ${W.preTitle?.fontSize || "24px"};
            line-height: ${W.preTitle?.lineHeight || "normal"};
            color: ${W.preTitle?.color || "#444c3c"};
            letter-spacing: ${W.preTitle?.letterSpacing || "0.0192px"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${W.preTitle?.mobileFontSize || "20px"};
                        letter-spacing: ${W.preTitle?.mobileLetterSpacing || "0.016px"};
                    `,
                    css`
                        font-size: ${W.preTitle?.mobileFontSize || "20px"};
                        letter-spacing: ${W.preTitle?.mobileLetterSpacing || "0.016px"};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        css: css`
            font-family: ${W.title?.fontFamily || "'EB Garamond', serif"};
            font-weight: ${W.title?.fontWeight || "500"};
            font-size: ${W.title?.fontSize || "32px"};
            line-height: ${W.title?.lineHeight || "normal"};
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${W.title?.mobileFontSize || "28px"};
                    `,
                    css`
                        font-size: ${W.title?.mobileFontSize || "28px"};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    gridContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${W.grid?.gap || "32px"};
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: ${W.mobileGap || "21px"};
                    `,
                    css`
                        gap: ${W.mobileGap || "21px"};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    gridRow: {
        css: css`
            display: flex;
            gap: ${W.grid?.gap || "32px"};
            justify-content: center;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        gap: ${W.grid?.mobileGap || "16px"};
                    `,
                    css`
                        gap: ${W.grid?.mobileGap || "16px"};
                    `,
                    css`
                        flex-wrap: wrap;
                    `,
                    css`
                        flex-wrap: wrap;
                    `,
                    null,
                ])}
        `,
    },
    swatchCard: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.card?.gap || "12px"};
            align-items: center;
            width: ${W.grid?.desktopCardWidth || "300px"};
            flex-shrink: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: calc(50% - ${parseInt(W.grid?.mobileGap || "16", 10) / 2}px);
                        flex: 0 0 auto;
                    `,
                    css`
                        width: calc(50% - ${parseInt(W.grid?.mobileGap || "16", 10) / 2}px);
                        flex: 0 0 auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    swatchImageWrapper: {
        css: css`
            width: 100%;
            aspect-ratio: ${W.image?.aspectRatio || "1 / 1"};
            position: relative;
            overflow: hidden;
        `,
    },
    swatchLabel: {
        css: css`
            font-family: ${W.label?.fontFamily || "'Manrope', sans-serif"};
            font-weight: ${W.label?.fontWeight || "500"};
            font-size: ${W.label?.fontSize || "15px"};
            line-height: ${W.label?.lineHeight || "1.5"};
            letter-spacing: ${W.label?.letterSpacing || "0.08px"};
            color: ${getColor("text.main")};
            text-align: center;
            width: 100%;
        `,
    },
    carouselWrapper: {
        css: css`
            width: calc(100% + ${W.carousel?.overflowExtend || "16px"});
            position: relative;
        `,
    },
    carouselViewport: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 ${W.carousel?.slideWidth || "200px"};
            min-width: 0;
            margin-right: ${W.carousel?.slideMargin || "20px"};
            display: flex;
            flex-direction: column;
            gap: ${W.card?.gap || "12px"};
            align-items: center;
        `,
    },
    ctaWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
        `,
    },
    ctaButton: {
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: ${W.cta?.height || "44px"};
            padding: ${W.cta?.padding || "10px 24px"};
            background-color: ${getColor("common.background")};
            border: 1px solid ${getColor("text.main")};
            color: ${getColor("text.main")};
            font-family: ${W.cta?.fontFamily || "'Manrope', sans-serif"};
            font-weight: ${W.cta?.fontWeight || "500"};
            font-size: ${W.cta?.fontSize || "15px"};
            line-height: ${W.cta?.lineHeight || "1.5"};
            letter-spacing: ${W.cta?.letterSpacing || "0.08px"};
            text-decoration: none;
            cursor: pointer;
            white-space: nowrap;
            box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.05);
            transition: all 0.3s ease;

            &:hover {
                background-color: ${getColor("text.main")};
                border-color: ${getColor("text.main")};
                color: ${getColor("common.background")};
            }
        `,
    },
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const SurSwatchGrid: React.FC<SurSwatchGridProps> = ({ fields }) => {
    const styles = surSwatchGridStyles;
    const ctaLink = useGetLink(fields.ctaLink);
    const mobileDisplayMode = fields.mobileDisplayMode || "grid";
    const bgColor = fields.backgroundColor || "";
    const items = fields.items || [];

    // Embla carousel (for mobile carousel mode)
    const [emblaRef, embla] = useEmblaCarousel();

    useEffect(() => {
        if (!embla) return;

        embla.reInit({
            align: "start",
            dragFree: false,
            loop: true,
        });
    }, [embla, items]);

    // Desktop: split items into rows of 4
    const desktopCols = 4;
    const desktopRows: SwatchItemModel[][] = [];
    for (let i = 0; i < items.length; i += desktopCols) {
        desktopRows.push(items.slice(i, i + desktopCols));
    }

    // Mobile grid: split items into rows of 2
    const mobileCols = 2;
    const mobileRows: SwatchItemModel[][] = [];
    for (let i = 0; i < items.length; i += mobileCols) {
        mobileRows.push(items.slice(i, i + mobileCols));
    }

    const renderSwatchCard = (item: SwatchItemModel, index: number, cardStyle: InjectableCss | undefined) => (
        <StyledWrapper key={`swatch-${index}`} {...cardStyle}>
            <StyledWrapper {...styles.swatchImageWrapper}>
                {item.fields.image && (
                    <LazyImage
                        src={item.fields.image}
                        altText={item.fields.label || `Swatch ${index + 1}`}
                        imgProps={{ style: { width: "100%", height: "100%", objectFit: "cover" } }}
                    />
                )}
            </StyledWrapper>
            {item.fields.label && <Typography {...styles.swatchLabel}>{item.fields.label}</Typography>}
        </StyledWrapper>
    );

    const wrapperCss = {
        css: css`
            ${styles.wrapper?.css}
            ${bgColor ? `background-color: ${bgColor};` : ""}
            ${fields.customCSS}
        `,
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...wrapperCss}>
            {/* Header: preTitle + title */}
            {(fields.preTitle || fields.title) && (
                <StyledWrapper {...styles.headerWrapper}>
                    {fields.preTitle && <Typography {...styles.preTitle}>{fields.preTitle}</Typography>}
                    {fields.title && (
                        <Typography {...styles.title} as="h3">
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
            )}

            {/* Desktop grid: 4 per row, centered incomplete rows */}
            <Hidden below="lg">
                <StyledWrapper {...styles.gridContainer}>
                    {desktopRows.map((row, rowIndex) => (
                        <StyledWrapper key={`desktop-row-${rowIndex}`} {...styles.gridRow}>
                            {row.map((item, itemIndex) => renderSwatchCard(item, rowIndex * desktopCols + itemIndex, styles.swatchCard))}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Hidden>

            {/* Mobile: grid or carousel based on CMS field */}
            <Hidden above="md">
                {mobileDisplayMode === "carousel" ? (
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselViewport} ref={emblaRef}>
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                `}
                            >
                                {items.map((item, index) => (
                                    <StyledWrapper key={`carousel-${index}`} {...styles.carouselSlide}>
                                        <StyledWrapper {...styles.swatchImageWrapper}>
                                            {item.fields.image && (
                                                <LazyImage
                                                    src={item.fields.image}
                                                    altText={item.fields.label || `Swatch ${index + 1}`}
                                                    imgProps={{ style: { width: "100%", height: "100%", objectFit: "cover" } }}
                                                />
                                            )}
                                        </StyledWrapper>
                                        {item.fields.label && <Typography {...styles.swatchLabel}>{item.fields.label}</Typography>}
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.gridContainer}>
                        {mobileRows.map((row, rowIndex) => (
                            <StyledWrapper key={`mobile-row-${rowIndex}`} {...styles.gridRow}>
                                {row.map((item, itemIndex) => renderSwatchCard(item, rowIndex * mobileCols + itemIndex, styles.swatchCard))}
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                )}
            </Hidden>

            {/* CTA button */}
            {fields.ctaLabel && (
                <StyledWrapper {...styles.ctaWrapper}>
                    {ctaLink?.url ? (
                        <Link href={ctaLink.url}>
                            <StyledWrapper {...styles.ctaButton}>{fields.ctaLabel}</StyledWrapper>
                        </Link>
                    ) : (
                        <StyledWrapper {...styles.ctaButton}>{fields.ctaLabel}</StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const SurSwatchGridModule: WidgetModule = {
    component: SurSwatchGrid,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "Sur Swatch Grid",
        fieldDefinitions: [
            {
                name: fields.preTitle,
                displayName: "Pre-Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 0,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 1,
            },
            {
                name: fields.items,
                displayName: "Swatch Items",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.label || "Swatch";
                },
                defaultValue: [],
                fieldType: "General",
                tab: contentTab,
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Swatch Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: "label",
                        displayName: "Swatch Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                ],
            },
            {
                name: fields.ctaLabel,
                displayName: "CTA Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
                sortOrder: 3,
            },
            {
                name: fields.ctaLink,
                displayName: "CTA Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Page" } as LinkFieldValue,
                fieldType: "General",
                tab: contentTab,
                sortOrder: 4,
            },
            {
                name: fields.mobileDisplayMode,
                displayName: "Mobile Display Mode",
                editorTemplate: "DropDownField",
                defaultValue: "grid",
                options: [
                    { displayName: "Grid", value: "grid" },
                    { displayName: "Carousel", value: "carousel" },
                ],
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 0,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 1,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                defaultValue: "",
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 2,
            },
        ],
    },
};

export default SurSwatchGridModule;
